import { useNavigate } from "react-router-dom";
import { Wallet, Bot, TrendingUp, Clock, ArrowUpRight } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { PageHeader, Panel, StatCard } from "@/components/dashboard/primitives";
import { StatusBadge, fmtUSD } from "@/components/common";
import Loading from "@/components/dashboard/Loading";
import { useDashboard } from "@/hooks/useDashboard";
import { spendChart } from "@/data/demoData";

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-[#0B1526] px-3 py-2 text-xs">
      <p className="text-slate-400">{payload[0].payload.day}</p>
      <p className="text-white font-semibold">{fmtUSD(payload[0].value)}</p>
    </div>
  );
}

export default function Overview() {
  const navigate = useNavigate();
  const { data, isLoading } = useDashboard();

  if (isLoading || !data) {
    return (<div><PageHeader title="Overview" subtitle="Executive snapshot of your autonomous agent treasury." /><Loading /></div>);
  }

  const { stats, agents, transactions } = data;
  const activity = agents.map((a) => ({ name: a.name, spent: a.spent }));
  const maxSpend = Math.max(...activity.map((a) => a.spent), 1);
  const recent = transactions.slice(0, 5);

  return (
    <div>
      <PageHeader title="Overview" subtitle="Executive snapshot of your autonomous agent treasury." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Wallet} label="Total Balance" value={fmtUSD(stats.totalBalance)} testid="stat-total-balance" />
        <StatCard icon={Bot} label="Active Agents" value={String(stats.activeAgents).padStart(2, "0")} accent="text-[#4F8CFF]" testid="stat-active-agents" />
        <StatCard icon={TrendingUp} label="Monthly Spend" value={fmtUSD(stats.monthlySpend)} testid="stat-monthly-spend" />
        <StatCard icon={Clock} label="Pending Approvals" value={String(stats.pendingApprovals).padStart(2, "0")} accent="text-amber-400" testid="stat-pending-approvals" />
      </div>

      <div className="mt-5 grid lg:grid-cols-3 gap-5">
        <Panel className="lg:col-span-2 p-6" data-testid="spend-chart">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-base font-semibold text-white">Spending this week</h3>
              <p className="text-xs text-slate-400 mt-0.5">Aggregated across all agents</p>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={spendChart} margin={{ left: -18, right: 6, top: 6 }}>
                <defs>
                  <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4F8CFF" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#4F8CFF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="day" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: "rgba(79,140,255,0.3)" }} />
                <Area type="monotone" dataKey="spend" stroke="#4F8CFF" strokeWidth={2.5} fill="url(#spendGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel className="p-6" data-testid="agent-activity">
          <h3 className="text-base font-semibold text-white mb-5">Agent activity</h3>
          <div className="space-y-4">
            {activity.map((a) => (
              <div key={a.name}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-300 truncate pr-2">{a.name}</span>
                  <span className="text-white font-medium">{fmtUSD(a.spent)}</span>
                </div>
                <div className="mt-1.5 h-1.5 rounded-full bg-white/8 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#4F8CFF] to-[#7C5CFF]" style={{ width: `${(a.spent / maxSpend) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel className="mt-5 p-6" data-testid="recent-transactions">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-white">Recent transactions</h3>
          <button onClick={() => navigate("/app/transactions")} className="text-sm text-[#4F8CFF] hover:underline flex items-center gap-1" data-testid="view-all-transactions">
            View all <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="space-y-2">
          {recent.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/6 bg-white/[0.02] px-4 py-3 hover:border-white/12 transition-colors">
              <div className="min-w-0">
                <p className="text-sm text-white truncate">{t.agent} <span className="text-slate-500">→</span> {t.merchant}</p>
                <p className="text-xs text-slate-500 mt-0.5">{t.date}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm font-medium text-white">{fmtUSD(t.amount)}</span>
                <StatusBadge status={t.status} />
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
